<template>
  <div class="bg-white dark:bg-gray-900 border-gray-100 dark:border-gray-700/50 rounded-2xl shadow-sm overflow-hidden">
    <!-- Basic Information Grid -->
    <div class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
      <div class="space-y-2">
        <label class="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
          {{ t('app.views.cashRegister.openedBy') }}
        </label>
        <div class="flex items-center gap-2">
          <div class="w-8 h-8 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center">
            <svg
              class="w-4 h-4 text-gray-500 dark:text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
              />
            </svg>
          </div>
          <div>
            <p class="font-medium text-gray-900 dark:text-gray-100 text-sm">
              {{ session.opened_by_name || t('app.common.unknown') }}
            </p>
          </div>
        </div>
      </div>
      <div class="space-y-2">
        <label class="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
          {{ t('app.views.cashRegister.initialBalance') }}
        </label>
        <div class="flex items-center gap-2">
          <div class="w-8 h-8 bg-green-50 dark:bg-green-900/20 rounded-full flex items-center justify-center">
            <svg
              class="w-4 h-4 text-green-600 dark:text-green-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
          <p class="font-semibold text-gray-900 dark:text-gray-100 text-lg">
            {{ formatCurrency(session.initial_balance) }}
          </p>
        </div>
      </div>
      <div class="space-y-2">
        <label class="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
          {{ t('app.views.cashRegister.openedAt') }}
        </label>
        <div class="flex items-center gap-2">
          <div class="w-8 h-8 bg-blue-50 dark:bg-blue-900/20 rounded-full flex items-center justify-center">
            <svg
              class="w-4 h-4 text-blue-600 dark:text-blue-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
          <div>
            <p class="font-medium text-gray-900 dark:text-gray-100 text-sm">
              {{ formatDate(session.opened_at) }}
            </p>
          </div>
        </div>
      </div>
      <div
        v-if="session.status === 'closed'"
        class="space-y-2"
      >
        <label class="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
          {{ t('app.views.cashRegister.duration') }}
        </label>
        <div class="flex items-center gap-2">
          <div class="w-8 h-8 bg-purple-50 dark:bg-purple-900/20 rounded-full flex items-center justify-center">
            <svg
              class="w-4 h-4 text-purple-600 dark:text-purple-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
          <div>
            <p class="font-medium text-gray-900 dark:text-gray-100 text-sm">
              {{ calculateDuration() }}
            </p>
          </div>
        </div>
      </div>
    </div>

    <!-- Summary Stats (only for closed sessions) -->
    <div
      v-if="session.status === 'closed'"
      class="border-t border-gray-100 dark:border-gray-700/50 pt-8"
    >
      <div class="flex items-center gap-2 mb-6">
        <div class="h-px bg-gray-200 dark:bg-gray-700 flex-1" />
        <h4 class="text-sm font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide">
          {{ t('app.views.cashRegister.sessionSummary') }}
        </h4>
        <div class="h-px bg-gray-200 dark:bg-gray-700 flex-1" />
      </div>
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div class="bg-gray-50 dark:bg-gray-900 rounded-xl p-4  border-gray-100 dark:border-gray-700/50">
          <div class="flex items-center justify-between mb-2">
            <span class="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              {{ t('app.views.cashRegister.totalSales') }}
            </span>
            <div class="w-6 h-6 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center">
              <svg
                class="w-3 h-3 text-green-600 dark:text-green-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"
                />
              </svg>
            </div>
          </div>
          <p class="text-xl font-bold text-gray-900 dark:text-gray-100">
            {{ formatCurrency(props.sessionSummary?.total_sales || 0) }}
          </p>
        </div>
        <div class="bg-gray-50 dark:bg-gray-900 rounded-xl p-4  border-gray-100 dark:border-gray-700/50">
          <div class="flex items-center justify-between mb-2">
            <span class="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              {{ t('app.views.cashRegister.totalExpenses') }}
            </span>
            <div class="w-6 h-6 bg-red-100 dark:bg-red-900/30 rounded-lg flex items-center justify-center">
              <svg
                class="w-3 h-3 text-red-600 dark:text-red-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M13 17h8m0 0V9m0 8l-8-8-4 4-6-6"
                />
              </svg>
            </div>
          </div>
          <p class="text-xl font-bold text-gray-900 dark:text-gray-100">
            {{ formatCurrency(props.sessionSummary?.total_expenses || 0) }}
          </p>
        </div>
        <div class="bg-gray-50 dark:bg-gray-900 rounded-xl p-4  border-gray-100 dark:border-gray-700/50">
          <div class="flex items-center justify-between mb-2">
            <span class="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              {{ t('app.views.cashRegister.netSales') }}
            </span>
            <div class="w-6 h-6 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
              <svg
                class="w-3 h-3 text-blue-600 dark:text-blue-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                />
              </svg>
            </div>
          </div>
          <p class="text-xl font-bold text-gray-900 dark:text-gray-100">
            {{ formatCurrency(props.sessionSummary?.net_sales || 0) }}
          </p>
        </div>
        <div class="bg-gray-50 dark:bg-gray-900 rounded-xl p-4  border-gray-100 dark:border-gray-700/50">
          <div class="flex items-center justify-between mb-2">
            <span class="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              {{ t('app.views.cashRegister.totalTransactions') }}
            </span>
            <div class="w-6 h-6 bg-purple-100 dark:bg-purple-900/30 rounded-lg flex items-center justify-center">
              <svg
                class="w-3 h-3 text-purple-600 dark:text-purple-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                />
              </svg>
            </div>
          </div>
          <p class="text-xl font-bold text-gray-900 dark:text-gray-100">
            {{ props.sessionSummary?.total_transactions || 0 }}
          </p>
        </div>
      </div>
    </div>

    <!-- Closing Details (only for closed sessions) -->
    <div
      v-if="session.status === 'closed'"
      class="border-t border-gray-100 dark:border-gray-700/50 pt-8"
    >
      <div class="flex items-center gap-2 mb-6">
        <div class="h-px bg-gray-200 dark:bg-gray-700 flex-1" />
        <h4 class="text-sm font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide">
          {{ t('app.views.cashRegister.closingDetails') }}
        </h4>
        <div class="h-px bg-gray-200 dark:bg-gray-700 flex-1" />
      </div>
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div class="space-y-2">
          <label class="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
            {{ t('app.views.cashRegister.closedBy') }}
          </label>
          <div class="flex items-center gap-2">
            <div class="w-8 h-8 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center">
              <svg
                class="w-4 h-4 text-gray-500 dark:text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                />
              </svg>
            </div>
            <div>
              <p class="font-medium text-gray-900 dark:text-gray-100 text-sm">
                {{ session.closed_by_name || t('app.common.unknown') }}
              </p>
            </div>
          </div>
        </div>
        <div class="space-y-2">
          <label class="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
            {{ t('app.views.cashRegister.closedAt') }}
          </label>
          <div class="flex items-center gap-2">
            <div class="w-8 h-8 bg-blue-50 dark:bg-blue-900/20 rounded-full flex items-center justify-center">
              <svg
                class="w-4 h-4 text-blue-600 dark:text-blue-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <div>
              <p class="font-medium text-gray-900 dark:text-gray-100 text-sm">
                {{ session.closed_at ? formatDate(session.closed_at) : t('app.common.na') }}
              </p>
            </div>
          </div>
        </div>
        <div class="space-y-2">
          <label class="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
            {{ t('app.views.cashRegister.finalBalance') }}
          </label>
          <div class="flex items-center gap-2">
            <div class="w-8 h-8 bg-green-50 dark:bg-green-900/20 rounded-full flex items-center justify-center">
              <svg
                class="w-4 h-4 text-green-600 dark:text-green-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <p class="font-semibold text-gray-900 dark:text-gray-100 text-lg">
              {{ formatCurrency(session.final_balance || 0) }}
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { formatCurrency } from '@/utils/priceHelpers';
import { formatDateTime as formatDate } from '@/utils/dateHelpers';
import type { Session } from './sessionDetailsTypes';

interface Props {
  session: Session;
  sessionSummary: any;
  getStatusBadgeClass: (status: string) => string;
}

const props = defineProps<Props>();
const { t } = useI18n();

const calculateDuration = () => {
  const opened = new Date(props.session.opened_at);
  const closed = props.session.closed_at ? new Date(props.session.closed_at) : new Date();
  const diff = closed.getTime() - opened.getTime();

  const hours = Math.floor(diff / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

  if (hours > 0) {
    return t('app.views.cashRegister.durationHours', { hours, minutes });
  }
  return t('app.views.cashRegister.durationMinutes', { minutes });
};
</script>
